import {
  BarChart3, TrendingUp, FileDown, FileText, ScrollText, AlertTriangle, Webhook,
  Activity, ClipboardList, Truck, DollarSign, Inbox, Users, ShieldCheck, Clock, BookOpen,
  Pill, MessageCircle, Building2, FileCheck, HelpCircle, Star, Sparkles, Package, Settings, Plane, Bot,
  Youtube, User, Search, Trophy, CreditCard, Repeat, FlaskConical,
} from "lucide-react";

/**
 * Sidebar / mobile-tab navigation config for the Admin Dashboard.
 *
 * Items with `subs` render a sub-tabs strip when their parent is active.
 * Item `key` either matches the active leaf directly (when no subs) or is a
 * grouping key that contains the active leaf in its `subs` array.
 */
const adminNavItems = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "myprofile", label: "My Profile", icon: User },
  {
    key: "reports", label: "Reports & Health", icon: TrendingUp,
    subs: [
      { key: "analytics", label: "Analytics", icon: TrendingUp },
      { key: "reports", label: "Reports", icon: FileDown },
      { key: "erx", label: "ERX Records", icon: FileText },
      { key: "audit", label: "Audit Logs", icon: ScrollText },
      { key: "suspicious", label: "Suspicious Activity", icon: AlertTriangle },
      { key: "lifefile-kpis", label: "Pharmacy Fulfilment", icon: Activity },
      { key: "health", label: "System Health", icon: Activity },
      { key: "errors", label: "Error Reports", icon: AlertTriangle },
    ],
  },
  {
    key: "orders-group", label: "Orders", icon: ClipboardList,
    subs: [
      { key: "orders", label: "Orders", icon: ClipboardList },
      { key: "tracking", label: "Tracking Queue", icon: Truck },
      { key: "delivery-exceptions", label: "Delivery Windows", icon: Plane },
      { key: "stripe-payments", label: "Payments", icon: CreditCard },
      { key: "subscriptions", label: "Subscriptions", icon: Repeat },
      { key: "payment-processor", label: "Payment Processor", icon: Repeat },
      { key: "refunds", label: "Pending Refunds", icon: DollarSign },
    ],
  },
  {
    key: "wellness-group", label: "Wellness", icon: Inbox,
    subs: [
      { key: "severe-symptoms", label: "Severe Symptoms", icon: AlertTriangle },
      { key: "unlock-requests", label: "Unlock Requests", icon: Inbox },
    ],
  },
  {
    key: "team", label: "Team", icon: Users,
    subs: [
      { key: "users", label: "Users", icon: Users },
      { key: "compliance", label: "Compliance", icon: ShieldCheck },
      { key: "timeclock", label: "Time Clock", icon: Clock },
      { key: "training-status", label: "Training Status", icon: BookOpen },
      { key: "sandbox-training", label: "Sandbox Training", icon: FlaskConical },
    ],
  },
  {
    key: "meds", label: "Medications", icon: Pill,
    subs: [
      { key: "medications", label: "Medications", icon: Pill },
      { key: "rxtemplates", label: "Rx Templates", icon: FileText },
      { key: "dispensetemplates", label: "Dispense Templates", icon: Package },
    ],
  },
  { key: "chat", label: "Live Chat", icon: MessageCircle, hidden: true },
  {
    key: "pharmacy-group", label: "Pharmacy Partners", icon: Building2,
    subs: [
      { key: "pharmacy", label: "Pharmacies", icon: Building2 },
      { key: "lifefile-config", label: "Polomar · LifeFile", icon: Webhook },
      { key: "manual-tracking", label: "Manual Tracking Upload", icon: Package },
      { key: "manual-subscriptions", label: "Manual Subscriptions", icon: CreditCard },
      { key: "coas", label: "COAs (Lot Lookup)", icon: FileCheck },
      { key: "shipping-study-page", label: "Shipping Study Page", icon: BookOpen },
      { key: "pharmacy-inquiries", label: "Affiliate Inquiries", icon: Inbox },
      { key: "baa", label: "BAAs", icon: FileCheck },
    ],
  },
  {
    key: "templates", label: "Templates", icon: FileText,
    subs: [
      { key: "chat-bot", label: "Chat Bot", icon: Bot },
      { key: "canned", label: "Chat Templates", icon: MessageCircle },
      { key: "emails", label: "Email Templates", icon: FileText },
      { key: "sms", label: "SMS Templates", icon: MessageCircle },
    ],
  },
  {
    key: "content", label: "Content", icon: FileDown,
    subs: [
      { key: "banners", label: "Banners", icon: FileDown },
      { key: "factmyth", label: "Fact or Myth", icon: HelpCircle },
      { key: "faq", label: "FAQ", icon: HelpCircle },
      { key: "youtube", label: "YouTube Library", icon: Youtube },
      { key: "reviews", label: "Reviews", icon: Star },
    ],
  },
  {
    key: "loyalty-marketing", label: "Loyalty & Marketing", icon: DollarSign,
    subs: [
      { key: "loyalty", label: "Loyalty & Coupons", icon: DollarSign },
      { key: "marketing", label: "Marketing Codes", icon: DollarSign },
      { key: "pricing", label: "Pricing", icon: DollarSign },
      { key: "tier-bullets", label: "Tier Bullets", icon: Sparkles },
      { key: "testimonials", label: "Testimonials", icon: Sparkles },
    ],
  },
  { key: "training", label: "Training Manual", icon: BookOpen },
  { key: "stepathon", label: "Step Rewards", icon: Trophy },
  {
    key: "system-ops", label: "System Ops", icon: ShieldCheck,
    subs: [
      { key: "system-ops", label: "Pause & integrity", icon: ShieldCheck },
      { key: "seo-and-crawlers", label: "SEO & Crawlers", icon: Search },
      { key: "ai-citations", label: "AI Citations", icon: Trophy },
      { key: "competitor-directory", label: "Competitors", icon: Building2 },
      { key: "id-verification-settings", label: "ID verification", icon: ShieldCheck },
    ],
  },
  { key: "settings", label: "Settings", icon: Settings },
];

export default adminNavItems;

/** Find the parent nav item for a given leaf-key. Defaults to first item. */
export function findMainForLeaf(leafKey) {
  for (const item of adminNavItems) {
    if (!item.subs && item.key === leafKey) return item;
    if (item.subs && item.subs.some((s) => s.key === leafKey)) return item;
  }
  return adminNavItems[0];
}
